import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from './ThemeContext';
import MainTableV2 from './MainTableV2';
import { useAuth } from './AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from './PageHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlus, faCheck, faExclamation, faTrash, faPaperPlane, faEye } from '@fortawesome/free-solid-svg-icons';
import SalesRepModal from './SalesRepModal';
import Spinner from './Spinner';
import MobileHeader from './MobileHeader';
import LogsModal from './showLogsModal';

const UserTable = () => {
  const { theme } = useTheme();
  const primaryColor = theme.primary;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { currentUser } = useAuth();
  const clearAlert = () => setAlertMessage(null);
  const navigate = useNavigate();

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [salesReps, setSalesReps] = useState([]);
  const [showAddRepModal, setShowAddRepModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logs, setLogs] = useState([]);
  const apiUrl = process.env.REACT_APP_API_URL;

  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${apiUrl}/getAdminUserData`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            userId: currentUser.userId, 
            requestType: 'getUserDetails', 
            subDomain: currentUser.subDomain 
          })
        });

        if (response.ok) {
          const fetchedData = await response.json();
          console.log('fetched admin users: ', fetchedData);
          setData(fetchedData.users || []);
          setSalesReps(fetchedData.salesReps || []);
        } else {
          setAlertMessage(`Server Error: ${await response.text()}`);
        }
      } catch (error) {
        setAlertMessage(`Error: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  const fetchUserLogs = async (userId) => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/dataServer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'getUserAuditLogsById',
          payload: {
            requestedUserId: userId,            
            userId: currentUser.userId
          }
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setLogs(result.logs);
        setShowLogsModal(true);
      } else {
        setAlertMessage(`Error: ${await response.text()}`);
      }
    } catch (error) {
      setAlertMessage(`Error fetching logs: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (action, row) => {
    setModalAction(action);
    setSelectedUser(row);
    setShowModal(true);
  };

  const handleEditClick = (row) => {
    navigate(`/EditUserForm/${row.RecordId}`, { state: { rowData: row } });
  };

  const handleAssignClick = (user) => {
    setSelectedUser(user);
    setShowAssignModal(true);
  };

  const handleAssignRep = async (repId) => {
    setLoading(true);
    try {
      const response = await fetch('https://fuellink-db-ui.azurewebsites.net/api/getAdminUserData', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: selectedUser.UserId,
          repId: repId,
          requestType: 'assignSalesRep'
        }),
      });

      if (response.ok) {
        const assignedRep = salesReps.find(rep => rep.Id === repId);
        setData(prevData =>
          prevData.map(user =>
            user.UserId === selectedUser.UserId ? { ...user, SalesRepId: repId, salesRepInfo: assignedRep } : user
          )
        );
        setShowAssignModal(false);
      } else {
        setAlertMessage(`Server Error: ${await response.text()}`);
      }
    } catch (error) {
      setAlertMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRep = async (salesRep) => {
    setLoading(true);
    try {
      const response = await fetch('https://fuellink-db-ui.azurewebsites.net/api/getAdminUserData', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestType: 'addSalesRep',
          subDomain: currentUser.subDomain,
          salesRep: salesRep,
          userId: currentUser.userId
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setSalesReps([...salesReps, result.salesRep]);
        setShowAddRepModal(false);
      } else {
        const errorText = await response.text();
        console.error(`Error adding sales rep: ${errorText}`);
      }
    } catch (error) {
      setAlertMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditRep = async (salesRep) => {
    setLoading(true);
    try {
      const response = await fetch('https://fuellink-db-ui.azurewebsites.net/api/getAdminUserData', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestType: 'editSalesRep',
          salesRep: salesRep,
          userId: currentUser.userId
        }),
      });

      if (response.ok) {
        setSalesReps(prevSalesReps =>
          prevSalesReps.map(rep =>
            rep.Id === salesRep.Id ? salesRep : rep
          )
        );
        setData(prevData =>
          prevData.map(user =>
            user.SalesRepId === salesRep.Id ? { ...user, salesRepInfo: salesRep } : user
          )
        );
      } else {
        const errorText = await response.text();
        console.error(`Error editing sales rep: ${errorText}`);
      }
    } catch (error) {
      setAlertMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const confirmModalAction = async () => {
    if (!selectedUser) return;
    setShowModal(false);
    setLoading(true);

    try {
      const requestBody = {
        action: modalAction === 'delete' ? 'deleteUser' : 'resendInvite',
        payload: {
          targetUserId: selectedUser.UserId,
          requestingUserId: currentUser.userId,
          invitationToken: selectedUser.InvitationToken
        },
      };

      const response = await fetch(`${apiUrl}/dataServer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();
      // console.log('Response: ', responseData);

      if (!responseData.success) {
        setAlertMessage(`${responseData.message}`);
        setLoading(false);
        return;
      }

      if (modalAction === 'delete') {
        // Remove the deleted user from the local state
        setData(prevData => prevData.filter(u => u.UserId !== selectedUser.UserId));
        setAlertMessage('Successfully deleted user.');
      } else {
        setData(prevData => 
          prevData.map(user => 
            user.InvitationToken === selectedUser.InvitationToken ? { ...user, InvitationToken: responseData.invitationToken } : user
          )
        );
        // console.log(data);
        setAlertMessage('Successfully resent invitation.');
      }
    } catch (error) {      
      setAlertMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRep = async (repToDelete, replaceWith) => {
    setLoading(true);
    try {
      const response = await fetch('https://fuellink-db-ui.azurewebsites.net/api/getAdminUserData', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          salesRepId: repToDelete.Id,
          replaceWith: replaceWith,
          requestType: 'deleteSalesRep'
        }),
      });

      if (response.ok) {
        setSalesReps(prevSalesReps => prevSalesReps.filter(rep => rep.Id !== repToDelete.Id));
        setData(prevData =>
          prevData.map(user =>
            user.SalesRepId === repToDelete.Id
              ? { ...user, SalesRepId: replaceWith, salesRepInfo: replaceWith ? salesReps.find(rep => rep.Id === replaceWith) : null }
              : user
          )
        );
      } else {
        const errorText = await response.text();
        throw new Error(`Error deleting sales rep: ${errorText}`);
      }
    } catch (error) {
      setAlertMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Updated Actions column with same functionality as the regular user table
  const columns = useMemo(() => [
    {
      name: 'Name',
      selector: row => `${row.FirstName} ${row.LastName}`,
      sortable: true,
      reorder: true,
      style: { whiteSpace: 'nowrap' }
    },
    {
      name: 'Company Name',
      selector: row => row.CompanyName,
      sortable: true,
      reorder: true,
      style: { whiteSpace: 'nowrap' }
    },
    {
      name: 'Email',
      selector: row => row.Email,
      sortable: true,
      reorder: true,
      style: { whiteSpace: 'nowrap' }
    },
    {
      name: 'Phone',
      selector: row => row.Phone,
      sortable: true,
      reorder: true,
      style: { whiteSpace: 'nowrap' }
    },
    {
      name: 'Location',
      selector: row => row.latestAgreement ? row.latestAgreement.LocationCityState : 'N/A',
      sortable: true,
      reorder: true,
      style: { whiteSpace: 'nowrap' }
    },
    {
      name: 'Last Activity',
      selector: row => row.lastActivity ? new Date(row.lastActivity).toLocaleString() : 'No Data',
      sortable: true,
      reorder: true,
    },
    {
      name: 'Setup Complete',
      cell: row => row.SetupComplete ? (
        <FontAwesomeIcon icon={faCheck} className="text-green-500" />
      ) : (
        <FontAwesomeIcon icon={faExclamation} className="text-red-500" />
      ),
      ignoreRowClick: true,
      style: { width: '50px', whiteSpace: 'nowrap' }
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="flex space-x-3">
          {/* Edit */}
          <FontAwesomeIcon
            icon={faEdit}
            className="themedIcon2xl cursor-pointer"
            title="Edit User"
            onClick={() => handleEditClick(row)}
          />
          {/* Resend Invite */}
          <FontAwesomeIcon
            icon={faPaperPlane}
            className={`themedIcon2xl ${row.UserId !== 'Pending' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            title='Resend Invite'
            onClick={() => {
              if (row.UserId === 'Pending') {
                openModal('resend', row);
              }
            }}
          />
          {/* Delete */}
          <FontAwesomeIcon
            icon={faTrash}
            className={`themedIcon2xl ${row.UserId === currentUser.userId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            title={row.UserId === currentUser.userId ? 'You cannot delete yourself' : 'Delete User'}
            onClick={() => {
              if (row.UserId !== currentUser.userId) {
                openModal('delete', row);
              }
            }}
          />
          {/* View Logs */}
          <FontAwesomeIcon
            icon={faEye}
            className="themedIcon2xl cursor-pointer"
            title="View Logs"
            onClick={() => fetchUserLogs(row.UserId)}
          />
        </div>
      ),
      ignoreRowClick: true,
    }
  ], [handleEditClick, theme.primary, currentUser.userId]);

  const filterConfig = [
    { key: 'FirstName', type: 'string' },
    { key: 'CompanyName', type: 'string' },
    { key: 'LastName', type: 'string' },
    { key: 'Email', type: 'string' },
    { key: 'Phone', type: 'string' },
    { key: 'latestAgreement.LocationCityState', type: 'string' },
    { key: 'salesRepInfo.FirstName', type: 'string' },
    { key: 'salesRepInfo.LastName', type: 'string' },
  ];

  return (
    <>
      <MobileHeader theme={theme} />
      <div className='mobile-container'>
        <PageHeader
          heading="User Management"
          context="Manage and administer user accounts and permissions."
        />

        <MainTableV2
          columns={columns}
          data={data}
          primaryColor={primaryColor}
          loading={loading}
          alertMessage={alertMessage}
          clearAlert={clearAlert}          
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filterConfig={filterConfig}
        >
          <div className="col-span-1"></div>  {/* Empty div for the first column */}
          <div className="col-span-1 flex justify-end">
            <Link className="themed-button" to="/adduser">Add New User</Link>
          </div>
        </MainTableV2>

        <SalesRepModal
          show={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          onAdd={handleAddRep}
          onAssign={handleAssignRep}
          currentUser={currentUser}
          selectedUser={selectedUser}
          salesReps={salesReps}
          setSalesReps={setSalesReps}
          onEdit={handleEditRep}
          onDelete={handleDeleteRep}
          setUsers={setData}
        />
      </div>
      {/* Logs Modal */}
      {showLogsModal && (
        <LogsModal
          logs={logs}
          onClose={() => setShowLogsModal(false)}
        />
      )}

      {/* Confirmation Modal */}
      {showModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-lg p-6 w-96">
              <h2 className="text-xl font-bold mb-4">
                {modalAction === 'delete' ? 'Delete User?' : 'Resend Invitation?'}
              </h2>
              <p className="mb-4">
                {modalAction === 'delete'
                  ? `Are you sure you want to delete "${selectedUser?.Email}"? This action can't be undone.`
                  : `Resend the invite to "${selectedUser?.Email}"?`}
              </p>
              <div className="flex justify-end space-x-2">
                <button
                  className="px-4 py-2 bg-gray-300 rounded"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="themed-button"
                  onClick={confirmModalAction}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
    </>
  );
};

export default UserTable;



// import React, { useState, useEffect, useMemo } from 'react';
// import { useTheme } from './ThemeContext';
// import MainTableV2 from './MainTableV2';
// import { useAuth } from './AuthContext';
// import { Link, useNavigate } from 'react-router-dom';
// import PageHeader from './PageHeader';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faEdit, faPlus, faCheck, faExclamation } from '@fortawesome/free-solid-svg-icons';
// import SalesRepModal from './SalesRepModal';
// import Spinner from './Spinner';
// import MobileHeader from './MobileHeader';
// import LogsModal from './showLogsModal';

// const UserTable = () => {
//   const { theme } = useTheme();
//   const primaryColor = theme.primary;
//   const [data, setData] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [alertMessage, setAlertMessage] = useState(null);
//   const [searchQuery, setSearchQuery] = useState('');
//   const { currentUser } = useAuth();
//   const clearAlert = () => setAlertMessage(null);
//   const navigate = useNavigate();

//   const [showAssignModal, setShowAssignModal] = useState(false);
//   const [selectedUser, setSelectedUser] = useState(null);
//   const [salesReps, setSalesReps] = useState([]);
//   const [showAddRepModal, setShowAddRepModal] = useState(false);
//   const [showLogsModal, setShowLogsModal] = useState(false);
//   const [logs, setLogs] = useState([]);
//   const apiUrl = process.env.REACT_APP_API_URL;

//   useEffect(() => {
//     const fetchData = async () => {
//       setLoading(true);
//       try {
//         const response = await fetch(`${apiUrl}/getAdminUserData`, {
//           method: 'POST',
//           headers: { 'Content-Type': 'application/json' },
//           body: JSON.stringify({ userId: currentUser.userId, requestType: 'getUserDetails', subDomain: currentUser.subDomain })
//         });

//         if (response.ok) {
//           const fetchedData = await response.json();
//           console.log(fetchedData);
//           setData(fetchedData.users || []);
//           setSalesReps(fetchedData.salesReps || []);
//         } else {
//           setAlertMessage(`Server Error: ${await response.text()}`);
//         }
//       } catch (error) {
//         setAlertMessage(`Error: ${error.message}`);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, [currentUser]);

//   const fetchUserLogs = async (userId) => {
//     setLoading(true);
//     try {
//       const response = await fetch(`${apiUrl}/dataServer`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({
//           action: 'getUserAuditLogsById',
//           payload: {
//             requestedUserId: userId,            
//             userId: currentUser.userId
//           }
//         }),
//       });

//       if (response.ok) {
//         const result = await response.json();
//         setLogs(result.logs);
//         setShowLogsModal(true);
//       } else {
//         setAlertMessage(`Error: ${await response.text()}`);
//       }
//     } catch (error) {
//       setAlertMessage(`Error fetching logs: ${error.message}`);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleEditClick = (row) => {
//     navigate(`/EditUserForm/${row.RecordId}`, { state: { rowData: row } });
//   };

//   const handleAssignClick = (user) => {
//     setSelectedUser(user);
//     setShowAssignModal(true);
//   };

//   const handleAssignRep = async (repId) => {
//     setLoading(true);
//     try {
//       const response = await fetch('https://fuellink-db-ui.azurewebsites.net/api/getAdminUserData', {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({
//           userId: selectedUser.UserId,
//           repId: repId,
//           requestType: 'assignSalesRep'
//         }),
//       });

//       if (response.ok) {
//         const assignedRep = salesReps.find(rep => rep.Id === repId);
//         setData(prevData =>
//           prevData.map(user =>
//             user.UserId === selectedUser.UserId ? { ...user, SalesRepId: repId, salesRepInfo: assignedRep } : user
//           )
//         );
//         setShowAssignModal(false);
//       } else {
//         setAlertMessage(`Server Error: ${await response.text()}`);
//       }
//     } catch (error) {
//       setAlertMessage(`Error: ${error.message}`);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleAddRep = async (salesRep) => {
//     setLoading(true);
//     try {
//       const response = await fetch('https://fuellink-db-ui.azurewebsites.net/api/getAdminUserData', {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({
//           requestType: 'addSalesRep',
//           subDomain: currentUser.subDomain,
//           salesRep: salesRep,
//           userId: currentUser.userId
//         }),
//       });

//       if (response.ok) {
//         const result = await response.json();
//         setSalesReps([...salesReps, result.salesRep]);
//         setShowAddRepModal(false);
//       } else {
//         const errorText = await response.text();
//         console.error(`Error adding sales rep: ${errorText}`);
//       }
//     } catch (error) {
//       setAlertMessage(`Error: ${error.message}`);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleEditRep = async (salesRep) => {
//     setLoading(true);
//     try {
//       const response = await fetch('https://fuellink-db-ui.azurewebsites.net/api/getAdminUserData', {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({
//           requestType: 'editSalesRep',
//           salesRep: salesRep,
//           userId: currentUser.userId
//         }),
//       });

//       if (response.ok) {
//         setSalesReps(prevSalesReps =>
//           prevSalesReps.map(rep =>
//             rep.Id === salesRep.Id ? salesRep : rep
//           )
//         );
//         setData(prevData =>
//           prevData.map(user =>
//             user.SalesRepId === salesRep.Id ? { ...user, salesRepInfo: salesRep } : user
//           )
//         );
//       } else {
//         const errorText = await response.text();
//         console.error(`Error editing sales rep: ${errorText}`);
//       }
//     } catch (error) {
//       setAlertMessage(`Error: ${error.message}`);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleDeleteRep = async (repToDelete, replaceWith) => {
//     setLoading(true);
//     try {
//       const response = await fetch('https://fuellink-db-ui.azurewebsites.net/api/getAdminUserData', {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({
//           salesRepId: repToDelete.Id, // Ensure the correct ID is being passed
//           replaceWith: replaceWith,
//           requestType: 'deleteSalesRep'
//         }),
//       });

//       if (response.ok) {
//         setSalesReps(prevSalesReps => prevSalesReps.filter(rep => rep.Id !== repToDelete.Id));
//         setData(prevData =>
//           prevData.map(user =>
//             user.SalesRepId === repToDelete.Id ? { ...user, SalesRepId: replaceWith, salesRepInfo: replaceWith ? salesReps.find(rep => rep.Id === replaceWith) : null } : user
//           )
//         );
//       } else {
//         const errorText = await response.text();
//         throw new Error(`Error deleting sales rep: ${errorText}`);
//       }
//     } catch (error) {
//       setAlertMessage(`Error: ${error.message}`);
//     } finally {
//       setLoading(false);
//     }
//   };


//   const columns = useMemo(() => [
//     {
//       name: 'Name',
//       selector: row => `${row.FirstName} ${row.LastName}`,
//       sortable: true,
//       reorder: true,
//       style: {
//         whiteSpace: 'nowrap'
//       }
//     },
//     {
//       name: 'Company Name',
//       selector: row => row.CompanyName,
//       sortable: true,
//       reorder: true,
//       style: {
//         whiteSpace: 'nowrap'
//       }
//     },
//     {
//       name: 'Email',
//       selector: row => row.Email,
//       sortable: true,
//       reorder: true,
//       style: {
//         whiteSpace: 'nowrap'
//       }
//     },
//     {
//       name: 'Phone',
//       selector: row => row.Phone,
//       sortable: true,
//       reorder: true,
//       style: {
//         whiteSpace: 'nowrap'
//       }
//     },
//     {
//       name: 'Location',
//       selector: row => row.latestAgreement ? row.latestAgreement.LocationCityState : 'N/A',
//       sortable: true,
//       reorder: true,
//       style: {
//         whiteSpace: 'nowrap'
//       }
//     },
//     // {
//     //   name: 'Assigned',
//     //   cell: row => row.salesRepInfo ? (
//     //     <>
//     //       {`${row.salesRepInfo.FirstName} ${row.salesRepInfo.LastName}`}
//     //       <FontAwesomeIcon icon={faEdit} className="ml-2 text-blue-500 cursor-pointer" onClick={() => handleAssignClick(row)} />
//     //     </>
//     //   ) : (
//     //     <span
//     //       onClick={() => handleAssignClick(row)}
//     //       className="text-blue-500 cursor-pointer"
//     //     >
//     //       <FontAwesomeIcon icon={faPlus} /> Assign
//     //     </span>
//     //   ),
//     //   sortable: true,
//     //   reorder: true,
//     //   style: {
//     //     whiteSpace: 'nowrap'
//     //   }
//     // },
//     {
//       name: 'Last Activity',
//       selector: row => row.lastActivity ? new Date(row.lastActivity).toLocaleString() : 'No Data',
//       sortable: true,
//       reorder: true,
//     },
//     {
//       name: 'Setup Complete',
//       cell: row => row.SetupComplete ? (
//         <FontAwesomeIcon icon={faCheck} className="text-green-500" />
//       ) : (
//         <FontAwesomeIcon icon={faExclamation} className="text-red-500" />
//       ),
//       ignoreRowClick: true,
//       style: {
//         width: '50px',
//         whiteSpace: 'nowrap'
//       }
//     },
//     {
//       name: 'Actions',
//       cell: row => (
//         <div>
//         <button
//           className="themed-button mr-2"
//           onClick={() => handleEditClick(row)}
//         >
//           Edit
//         </button>
//         <button
//           className="themed-button"
//           onClick={() => fetchUserLogs(row.UserId)}
//         >
//           View Logs
//         </button>
//         </div>
        
//       ),
//       ignoreRowClick: true,
//     }
//   ], [handleEditClick]);

//   const filterConfig = [
//     { key: 'FirstName', type: 'string' },
//     { key: 'CompanyName', type: 'string' },
//     { key: 'LastName', type: 'string' },
//     { key: 'Email', type: 'string' },
//     { key: 'Phone', type: 'string' },
//     { key: 'latestAgreement.LocationCityState', type: 'string' },
//     { key: 'salesRepInfo.FirstName', type: 'string' },
//     { key: 'salesRepInfo.LastName', type: 'string' },
//   ];

//   return (
//     <>
//       <MobileHeader theme={theme} />
//       <div className='mobile-container'>
//         <PageHeader
//           heading="User Management"
//           context="Manage and administer user accounts and permissions."
//         />

//         <MainTableV2
//           columns={columns}
//           data={data}
//           primaryColor={primaryColor}
//           loading={loading}
//           alertMessage={alertMessage}
//           clearAlert={clearAlert}          
//           searchQuery={searchQuery}
//           setSearchQuery={setSearchQuery}
//           filterConfig={filterConfig}
//         >
//           <div className="col-span-1"></div>  {/* Empty div for the first column */}
//           <div className="col-span-1 flex justify-end">
//             <Link className="themed-button" to="/adduser">Add New User</Link>
//           </div>

//         </MainTableV2>

//         <SalesRepModal
//           show={showAssignModal}
//           onClose={() => setShowAssignModal(false)}
//           onAdd={handleAddRep}
//           onAssign={handleAssignRep}
//           currentUser={currentUser}
//           selectedUser={selectedUser}
//           salesReps={salesReps}
//           setSalesReps={setSalesReps}
//           onEdit={handleEditRep}
//           onDelete={handleDeleteRep}
//           setUsers={setData}
//         />
//       </div>
//       {/* Logs Modal */}
//       {showLogsModal && (
//         <LogsModal
//           logs={logs}
//           onClose={() => setShowLogsModal(false)}
//         />
//       )}
//     </>
//   );
// };

// export default UserTable;
